import {Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {Subject} from 'rxjs';
import {finalize, takeUntil} from 'rxjs/operators';
import {MatTableDataSource} from '@angular/material/table';
import {MatPaginator, PageEvent} from '@angular/material/paginator';
import {MatDialog} from '@angular/material/dialog';
import {RulesEngineMetadataDTO} from '../../../models/RulesEngineMetadataDTO';
import {OtherRequestBuilder} from '../../../services/other-request-builder';
import {DateUtil} from '../../../../common/util/DateUtil';
import {TableControl} from '../../../../common/models/TableControl';
import {
  AddRulesEngineMetadataDialogComponent
} from '../../dialogs/add-rules-engine-metadata-dialog/add-rules-engine-metadata-dialog.component';
import {RulesEngineMetadataService} from '../../../services/rules-engine-metadata.service';
import {SharedService} from '../../../../common/services/shared-service';
import {PagedResponseDTO} from '../../../../common/models/PagedResponseDTO';
import {getSortDirection} from '../../../../common/util/Util';
import {Sort} from '@angular/material/sort';
import {detailsExpand} from '../../../../common';
import {UserAuthorisationService} from '../../../../common/services/user-authorisation.service';

enum TableColumns {
  Icons = 'Icons',
  RulesEngineMetadataId = 'rulesEngineMetadataId',
  ApplicationName = 'applicationName',
  ApplicationUrl = 'applicationUrl',
  ApplicationVersion = 'applicationVersion',
  Updated = 'updated',
  UpdatedBy = 'updatedBy',
}

@Component({
  selector: 'app-rules-engine-metadata',
  templateUrl: './rules-engine-metadata-management-view.component.html',
  styleUrls: [
    '../../../../../../styles/global/_management-styles.scss'
  ],
  animations: [detailsExpand]
})
export class RulesEngineMetadataManagementViewComponent implements OnInit, OnDestroy {
  readonly destroyed$ = new Subject();
  readonly dateFormat = DateUtil.dateFormat;
  readonly tableColumns = TableColumns;

  isBusyCounter = 0;
  tableControl: TableControl;
  displayedColumns: string[] = Object.values(TableColumns);
  dataSource: MatTableDataSource<RulesEngineMetadataDTO> = new MatTableDataSource();

  // MatPaginator
  @ViewChild('rulesEngineMetaDataPaginator', {static: false}) rulesEngineMetaDataPaginator: MatPaginator;

  constructor(public dialog: MatDialog,
              private sharedService: SharedService,
              private userAuthorisationService: UserAuthorisationService,
              private rulesEngineMetadataService: RulesEngineMetadataService) {
    const controls = OtherRequestBuilder.getTableControls();
    this.tableControl = new TableControl(
      controls.length,
      controls.pageSize,
      controls.pageNumber,
      controls.pageSizeOptions,
      'rulesEngineMetadataId',
      'DESCENDING',
      '',
      ''
    );

  }

  ngOnInit(): void {
    this.getListOfRulesEngineMetadata(this.tableControl);
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  pageChanged($event: PageEvent) {
    this.tableControl.pageNumber = $event.pageIndex;
    this.tableControl.pageSize = $event.pageSize;
    this.getListOfRulesEngineMetadata(this.tableControl);
  }

  sortData($event: Sort) {
    if (!$event.direction) {
      return;
    }
    if (this.rulesEngineMetaDataPaginator != null) {
      this.rulesEngineMetaDataPaginator.firstPage();
    }
    this.tableControl.pageNumber = 0;
    this.tableControl.sortField = $event.active;
    this.tableControl.sortDirection = getSortDirection($event.direction);
    this.getListOfRulesEngineMetadata(this.tableControl);
  }

  onEditRulesEngineMetadata(event: MouseEvent, rulesEngineMetadataId: bigint) {
    event.preventDefault();
    event.stopPropagation();
    if (!this.isAllowedToEdit()) {
      return
    }

    this.openDialog(rulesEngineMetadataId);
  }

  openDialog(rulesEngineMetadataId?: bigint) {
    const dialogRef = this.dialog.open(AddRulesEngineMetadataDialogComponent, {
      width: '40vw',
      data: {rulesEngineMetadataId},
      disableClose: true
    });

    dialogRef.afterClosed().subscribe(
      (result) => {
        if (result.isSaved) {
          this.getListOfRulesEngineMetadata(this.tableControl);
        }
      },
      (error) => {
        this.sharedService.openErrorDialog(error.message);
      }
    );
  }

  isAllowedToEdit(): boolean {
    let hasAccess = false;
    const hasRoleOperationsManager = this.userAuthorisationService.hasRoleOperationsManager();
    const hasOnBoardingTeamLeader = this.userAuthorisationService.hasOnBoardingTeamLeader();

    if (hasRoleOperationsManager) {
      hasAccess = true;
    }
    else if (hasOnBoardingTeamLeader) {
      hasAccess = true;
    }
    return hasAccess;
  }

  private getListOfRulesEngineMetadata(tableControl: TableControl): void {
    const pagedRequestDTO = OtherRequestBuilder.getTblRulesEngineMetadataPagedRequestDTO(tableControl);
    this.isBusyCounter++;
    this.rulesEngineMetadataService
      .getListOfRulesEngineMetadata(pagedRequestDTO)
      .pipe(
        takeUntil(this.destroyed$),
        finalize(() => {
          this.isBusyCounter--;
        })
      )
      .subscribe(
        (response: PagedResponseDTO<RulesEngineMetadataDTO>) => {
          this.dataSource.data = response.resultList;
          this.tableControl.length = response.totalRecords;
        },
        (errorResponse) => {
          const error = errorResponse.error;
          this.sharedService.openErrorDialog(error.businessMessage);
        }
      );
  }

}
